import CloseIcon from '@mui/icons-material/Close';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, SxProps, Theme, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { Control, useFieldArray } from 'react-hook-form';

import { CommonPageForm } from '../../../../../../../page-builder-v2.types';
import { CreateReviewCard } from '../create-review-card';
import { ReviewCard } from '../review-card';
import { styles } from './reviews-form.styles';

interface Props {
  control: Control<CommonPageForm>;
  open: boolean;
  onClose: () => void;
  productIdx?: number;
}

const ReviewsForm = ({ control, onClose, open, productIdx }: Props) => {
  const { append, fields, move, remove } = useFieldArray({
    control,
    name: `products.${productIdx}.reviews.reviews`,
  });
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from !== to) move(from, to);
  };

  if (!enabled) return null;

  return (
    <Box sx={styles.wrapper}>
      <Dialog fullWidth maxWidth="xs" onClose={onClose} open={open}>
        <DialogTitle sx={styles.dialogTitle}>
          Add Review
          <IconButton onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <CreateReviewCard
            onChange={(review) => {
              append(review);
              onClose();
            }}
          />
        </DialogContent>
      </Dialog>

      {fields.length > 0 ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable direction="horizontal" droppableId="reviews-droppable">
            {(provided) => (
              <Box ref={provided.innerRef} sx={styles.list} {...provided.droppableProps}>
                {fields.map((elem, reviewIdx) => (
                  <Draggable key={elem.id} draggableId={elem.id} index={reviewIdx}>
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={provided.draggableProps.style}
                        sx={[styles.cardWrapper, snapshot.isDragging && styles.cardWrapperDragging] as SxProps<Theme>}
                      >
                        <ReviewCard
                          control={control}
                          onDelete={() => remove(reviewIdx)}
                          productIdx={productIdx}
                          reviewIdx={reviewIdx}
                        />
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <Box sx={styles.emptyState}>
          <RateReviewOutlinedIcon sx={{ fontSize: 36 }} />
          <Typography variant="body2">No reviews added yet</Typography>
        </Box>
      )}
    </Box>
  );
};

export { ReviewsForm };
